import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EndpointsService } from "../services/endpoints/endpoints.service";
import {
	AlertThresholdMeasurementType,
	buildDefaultAlertThresholdsFromLimits,
	ParticipantAlertThresholdRow,
} from "../models/entities/participant-alert-threshold.model";
import {
	BIOMETRIC_THRESHOLD_SUBTYPES,
	type ParticipantAlertThresholdUpdateBody,
	type PatientBiometricThresholdDto,
} from "../models/endpoints/participant-alert-thresholds-endpoint.model";
import { useThresholds } from "./useThresholds";

const queryKey = (participantId: number) => ["participant-biometric-thresholds", participantId];

type CustomRow = {
	criticallyHigh: string;
	criticallyLow: string;
	ids: number[];
};

function parseBloodPressure(value: string): { systolic: number | null; diastolic: number | null } {
	const parts = value
		.trim()
		.split("/")
		.map((p) => p.trim());
	const sys = parts[0] ? Number(parts[0]) : null;
	const dia = parts[1] ? Number(parts[1]) : null;
	return {
		systolic: sys != null && !Number.isNaN(sys) ? sys : null,
		diastolic: dia != null && !Number.isNaN(dia) ? dia : null,
	};
}

/** Map backend DTO list (biometricType, biometricSubtype, thresholdValue) to custom row data per measurement type */
function mapDtoListToCustomRows(
	list: PatientBiometricThresholdDto[]
): Map<AlertThresholdMeasurementType, CustomRow> {
	const bySubtype = new Map<string, { value: number; id: number }>();
	for (const dto of list) {
		if (dto.biometricSubtype != null && dto.thresholdValue != null && dto.id != null) {
			bySubtype.set(dto.biometricSubtype, { value: Number(dto.thresholdValue), id: dto.id });
		}
	}

	const result = new Map<AlertThresholdMeasurementType, CustomRow>();

	const sysHigh = bySubtype.get(BIOMETRIC_THRESHOLD_SUBTYPES.BLOOD_PRESSURE_SYSTOLIC_HIGH);
	const diaHigh = bySubtype.get(BIOMETRIC_THRESHOLD_SUBTYPES.BLOOD_PRESSURE_DIASTOLIC_HIGH);
	const sysLow = bySubtype.get(BIOMETRIC_THRESHOLD_SUBTYPES.BLOOD_PRESSURE_SYSTOLIC_LOW);
	const diaLow = bySubtype.get(BIOMETRIC_THRESHOLD_SUBTYPES.BLOOD_PRESSURE_DIASTOLIC_LOW);
	if (sysHigh ?? diaHigh ?? sysLow ?? diaLow) {
		const criticallyHigh =
			sysHigh?.value != null && diaHigh?.value != null
				? `${sysHigh.value}/${diaHigh.value}`
				: "-";
		const criticallyLow =
			sysLow?.value != null && diaLow?.value != null
				? `${sysLow.value}/${diaLow.value}`
				: "-";
		const ids = [sysHigh?.id, diaHigh?.id, sysLow?.id, diaLow?.id].filter(
			(id): id is number => id != null
		);
		result.set("BLOOD_PRESSURE", { criticallyHigh, criticallyLow, ids });
	}

	const gluHigh = bySubtype.get(BIOMETRIC_THRESHOLD_SUBTYPES.GLUCOSE_HIGH);
	const gluLow = bySubtype.get(BIOMETRIC_THRESHOLD_SUBTYPES.GLUCOSE_LOW);
	if (gluHigh ?? gluLow) {
		result.set("GLUCOSE", {
			criticallyHigh: gluHigh?.value != null ? String(gluHigh.value) : "-",
			criticallyLow: gluLow?.value != null ? String(gluLow.value) : "-",
			ids: [gluHigh?.id, gluLow?.id].filter((id): id is number => id != null),
		});
	}

	return result;
}

function mergeWithDefaults(
	participantList: PatientBiometricThresholdDto[],
	defaultRows: Omit<ParticipantAlertThresholdRow, "isCustom">[]
): ParticipantAlertThresholdRow[] {
	const customByType = mapDtoListToCustomRows(participantList);
	return defaultRows.map((defaultRow) => {
		const custom = customByType.get(defaultRow.measurementType);
		const isCustom = !!custom && custom.ids.length > 0;
		return {
			...defaultRow,
			id: custom?.ids?.[0],
			ids: custom?.ids,
			criticallyHigh: custom?.criticallyHigh ?? defaultRow.criticallyHigh,
			criticallyLow: custom?.criticallyLow ?? defaultRow.criticallyLow,
			isCustom,
		};
	});
}

export function useParticipantAlertThresholds(participantId: number) {
	const queryClient = useQueryClient();
	const { data: limits } = useThresholds();

	const defaultRows = useMemo(() => buildDefaultAlertThresholdsFromLimits(limits), [limits]);

	const query = useQuery({
		queryKey: queryKey(participantId),
		queryFn: () =>
			EndpointsService.dataRetriever.getParticipantBiometricThresholds({ participantId }),
		enabled: !!participantId,
	});

	const participantList = Array.isArray(query.data) ? query.data : [];
	const rows: ParticipantAlertThresholdRow[] = useMemo(
		() => mergeWithDefaults(participantList, defaultRows),
		[participantList, defaultRows]
	);

	const updateMutation = useMutation({
		mutationFn: async (body: ParticipantAlertThresholdUpdateBody) => {
			const biometricType = body.measurementType;
			const isUpdate = body.ids != null && body.ids.length > 0;

			if (body.measurementType === "BLOOD_PRESSURE") {
				const high = parseBloodPressure(body.criticallyHigh ?? "-");
				const low = parseBloodPressure(body.criticallyLow ?? "-");
				const dtos: { subtype: string; value: number }[] = [
					{
						subtype: BIOMETRIC_THRESHOLD_SUBTYPES.BLOOD_PRESSURE_SYSTOLIC_HIGH,
						value: high.systolic ?? 0,
					},
					{
						subtype: BIOMETRIC_THRESHOLD_SUBTYPES.BLOOD_PRESSURE_DIASTOLIC_HIGH,
						value: high.diastolic ?? 0,
					},
					{
						subtype: BIOMETRIC_THRESHOLD_SUBTYPES.BLOOD_PRESSURE_SYSTOLIC_LOW,
						value: low.systolic ?? 0,
					},
					{
						subtype: BIOMETRIC_THRESHOLD_SUBTYPES.BLOOD_PRESSURE_DIASTOLIC_LOW,
						value: low.diastolic ?? 0,
					},
				];
				if (isUpdate && body.ids!.length >= 4) {
					for (let i = 0; i < 4; i++) {
						await EndpointsService.dataRetriever.updateParticipantBiometricThreshold({
							participantId,
							id: body.ids![i],
							body: {
								biometricType,
								biometricSubtype: dtos[i].subtype,
								thresholdValue: dtos[i].value,
							},
						});
					}
				} else {
					for (const d of dtos) {
						await EndpointsService.dataRetriever.createParticipantBiometricThreshold({
							participantId,
							body: {
								biometricType,
								biometricSubtype: d.subtype,
								thresholdValue: d.value,
							},
						});
					}
				}
				return;
			}

			// GLUCOSE
			const highVal = body.criticallyHigh != null ? Number(body.criticallyHigh) : 0;
			const lowVal = body.criticallyLow != null ? Number(body.criticallyLow) : 0;
			if (isUpdate && body.ids!.length >= 2) {
				await EndpointsService.dataRetriever.updateParticipantBiometricThreshold({
					participantId,
					id: body.ids![0],
					body: {
						biometricType,
						biometricSubtype: BIOMETRIC_THRESHOLD_SUBTYPES.GLUCOSE_HIGH,
						thresholdValue: highVal,
					},
				});
				await EndpointsService.dataRetriever.updateParticipantBiometricThreshold({
					participantId,
					id: body.ids![1],
					body: {
						biometricType,
						biometricSubtype: BIOMETRIC_THRESHOLD_SUBTYPES.GLUCOSE_LOW,
						thresholdValue: lowVal,
					},
				});
			} else {
				await EndpointsService.dataRetriever.createParticipantBiometricThreshold({
					participantId,
					body: {
						biometricType,
						biometricSubtype: BIOMETRIC_THRESHOLD_SUBTYPES.GLUCOSE_HIGH,
						thresholdValue: highVal,
					},
				});
				await EndpointsService.dataRetriever.createParticipantBiometricThreshold({
					participantId,
					body: {
						biometricType,
						biometricSubtype: BIOMETRIC_THRESHOLD_SUBTYPES.GLUCOSE_LOW,
						thresholdValue: lowVal,
					},
				});
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKey(participantId) });
		},
	});

	const hasAnyCustom = rows.some((r) => r.isCustom);

	return {
		rows,
		hasAnyCustom,
		isLoading: query.isPending,
		isError: query.isError,
		refetch: query.refetch,
		updateThreshold: async (body: ParticipantAlertThresholdUpdateBody) => {
			await updateMutation.mutateAsync(body);
		},
		isUpdating: updateMutation.isPending,
	};
}
